import { Alert as AntdAlert, App, Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../hooks/useAuth';
import { useAppStore } from '../../../store/store';
import {
  alertSeverityToAntdType,
  alertSupportsTotpCta,
  getAlertMessage,
  isAlertDismissBlockedByMandatoryTotp,
} from '../../../utils/alertMessages';
import { openTotpFormModal } from '../../../utils/openTotpFormModal';
import AlertDismissForm from '../../Forms/AlertDismissForm';
import AlertsListForm from '../../Forms/AlertsListForm';

/**
 * Server-driven alerts strip under the main header (GET /alerts/active).
 * Displays the highest priority active alert (if any),
 * including CTAs for dismiss, manage 2FA, and viewing more alerts.
 * Uses antd's Alert banner along with global modal context.
 */
export default function GlobalBanner() {
  // i18n translation hook
  const { t } = useTranslation();

  // Antd modal instance for modal popups
  const { modal } = App.useApp();

  // Custom auth hook to retrieve user info utility
  const { getUserInfo } = useAuth();

  // Global state selector for current active system alerts
  const activeAlerts = useAppStore((s) => s.activeAlerts);
  // Whether the TOTP_REQUIRED policy grace period has elapsed (mandatory enrollment gate is open)
  const isTotpMandatory = useAppStore((s) => s.appEvents.activateTotp.isMandatory);

  // If there are no active alerts, render nothing
  if (activeAlerts.length === 0) {
    return null;
  }

  // Grab the highest-priority (first) alert to show in the banner
  const primary = activeAlerts[0]!;

  // Render alert message via utility, translating as needed
  const message = getAlertMessage(primary, t);

  // Map alert's severity to Ant Design's Alert type
  const antdType = alertSeverityToAntdType(primary.severity as string);

  // Determine how many more non-primary alerts are present
  const moreCount = activeAlerts.length - 1;

  /**
   * Handler to open the dismiss dialog for the primary alert.
   * Pops up a confirm modal containing the AlertDismissForm.
   */
  const openDismissForPrimary = (): void => {
    const inst = modal.confirm({
      title: t('modal.alertDismiss.title'),
      content: (
        <AlertDismissForm
          alert={primary}
          onSuccess={() => inst.destroy()}
          onCancel={() => inst.destroy()}
        />
      ),
      footer: null,
      width: 480,
    });
  };

  /**
   * Handler to open a modal listing all active alerts.
   * Pops up a confirm modal containing the AlertsListForm.
   */
  const openAllAlerts = (): void => {
    const inst = modal.confirm({
      title: t('modal.alertsList.title'),
      content: <AlertsListForm getUserInfo={getUserInfo} onAllCleared={() => inst.destroy()} />,
      footer: null,
      width: 720,
    });
  };

  return (
    <AntdAlert
      banner
      className="mb-0 rounded-none border-x-0 border-t-0 transition-all duration-300"
      closable={false}
      type={antdType}
      title={
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Main alert message */}
          <span className="min-w-0 flex-1">{message}</span>
          {/* CTA section: show 2FA, "More", Dismiss where appropriate */}
          <Space className="shrink-0" size="small" wrap>
            {/* Show "Manage Two-Factor" if alert supports TOTP */}
            {alertSupportsTotpCta(primary.code) && (
              <Button
                size="small"
                type="text"
                onClick={() => openTotpFormModal({ modal, t, getUserInfo })}
              >
                {t('common.alerts.manageTwoFactor')}
              </Button>
            )}
            {/* Show "More" if additional alerts exist */}
            {moreCount > 0 && (
              <Button size="small" type="text" onClick={openAllAlerts}>
                {t('common.alerts.moreCount', { count: moreCount })}
              </Button>
            )}
            {/* Show "Dismiss" if alert is dismissable, unless mandatory TOTP enrollment blocks it */}
            {primary.dismissable &&
              !isAlertDismissBlockedByMandatoryTotp(primary.code, isTotpMandatory) && (
                <Button size="small" type="text" onClick={openDismissForPrimary}>
                  {t('common.alerts.dismissMenu')}
                </Button>
              )}
          </Space>
        </div>
      }
    />
  );
}
